<script setup>
import { computed, reactive, ref } from 'vue'
import { Ghost, GitCompareArrows } from 'lucide-vue-next'
import moment from 'moment'
import { Link } from '@inertiajs/vue3'
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@codinglabsau/gooey'

const props = defineProps({
  versionable: {
    type: Object,
    required: true,
  },
  currentUrl: {
    type: String,
    required: true,
  },
  versionIds: {
    type: Array,
    required: true,
  },
})

const emits = defineEmits(['update:version'])

const selectVersion = (leftVersion, rightVersion = null) => {
  emits('update:version', leftVersion, rightVersion)
  sheetOpen.value = false
}

const isInitialVersion = (version) => version.old_values.length === 0

const changelog = (version) => {
  if (version.old_values.length === 0) {
    return 'This is an original record.'
  }

  return Object.keys(version.new_values)
    .filter((key) => key !== 'slug')
    .map((key) => {
      let keyName = key

      if (key.endsWith('_id')) {
        keyName = key.replace(/_id$/, '')
      }

      keyName = keyName.replace(/_/g, ' ')

      return keyName
    })
    .join(', ')
}

const leftVersionId = computed(() => props.versionIds[0])
const rightVersionId = computed(() => props.versionIds[1] ?? null)
const comparingVersions = computed(() => !!(leftVersionId.value && rightVersionId.value))

const sheetOpen = ref(false)

const form = reactive({
  leftVersionId: leftVersionId.value ?? props.versionable.current_version,
  rightVersionId: rightVersionId.value ?? props.versionable.current_version - 1,
})

const isCurrentVersion = (version) => version === props.versionable.current_version
const isLeftVersion = (version) => version === leftVersionId.value

const isRightVersion = (version) => {
  if (!rightVersionId.value) {
    return false
  }

  return version === rightVersionId.value
}
</script>

<template>
  <Sheet :open="sheetOpen" @update:open="sheetOpen = !sheetOpen">
    <SheetContent>
      <SheetTitle class="flex items-center space-x-2 text-xl">
        {{ versionable.versions.length }}
        {{ versionable.versions.length === 1 ? 'version' : 'versions' }} found
      </SheetTitle>

      <SheetDescription>
        <template v-if="versionable.versions.length === 1">
          Only one version exists for this record.
        </template>

        <template v-if="versionable.versions.length > 1">
          Select two versions to compare.

          <div class="mt-4 space-y-4">
            <Button as-child variant="outline" class="font-bold text-foreground">
              <Link :href="currentUrl"> Go to current version</Link>
            </Button>

            <div class="flex items-center space-x-1">
              <Select v-model="form.leftVersionId">
                <SelectTrigger class="w-[80px]">
                  <SelectValue class="text-foreground"> v{{ form.leftVersionId }}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="version in versionable.versions"
                    :key="version.id"
                    :value="version.version"
                  >
                    v{{ version.version }}
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select v-model="form.rightVersionId">
                <SelectTrigger class="w-[80px]">
                  <SelectValue class="text-foreground"> v{{ form.rightVersionId }}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="version in versionable.versions"
                    :key="version.id"
                    :value="version.version"
                  >
                    v{{ version.version }}
                  </SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                class="font-bold text-foreground"
                @click="selectVersion(form.leftVersionId, form.rightVersionId)"
              >
                Compare
              </Button>
            </div>
          </div>
        </template>

        <hr class="my-4" />

        <ul class="mt-4 divide-y">
          <li
            v-for="item in versionable.versions"
            :key="item.id"
            class="flex w-full items-start justify-between space-x-2 py-2"
            :class="{
              'text-foreground':
                (isCurrentVersion(item.version) && !comparingVersions) ||
                isLeftVersion(item.version) ||
                isRightVersion(item.version),
            }"
          >
            <Badge
              variant="outline"
              :class="{
                'text-green-700': isLeftVersion(item.version),
                'text-red-400': isRightVersion(item.version),
              }"
            >
              v{{ item.version }}
            </Badge>

            <div class="grow">
              <div v-if="item.user" class="font-bold">{{ item.user?.name }}</div>

              <div v-else class="flex items-center font-bold">
                <Ghost class="mr-2 size-4 text-muted-foreground" />
                Original record
              </div>

              <div v-if="isInitialVersion(item)" class="mt-2 w-full text-xs italic">
                This is the initial record.
              </div>

              <div v-else class="mt-2 w-full text-xs italic">Changes: {{ changelog(item) }}</div>
            </div>

            <time class="whitespace-nowrap text-xs">
              {{ moment(item.created_at).format('DD MMM, YYYY') }}
            </time>
          </li>
        </ul>
      </SheetDescription>
    </SheetContent>
  </Sheet>

  <Button variant="outline" class="gap-x-1" @click="sheetOpen = !sheetOpen">
    <GitCompareArrows class="size-5" />

    <span v-if="!leftVersionId">
      History
      <span class="text-muted-foreground">
        ({{ versionable.versions.length }}
        {{ versionable.versions.length === 1 ? 'version' : 'versions' }})
      </span>
    </span>

    <div v-else class="flex items-center space-x-1">
      <div class="mr-1">Comparing:</div>
      <Badge variant="outline" class="text-green-700">v{{ form.leftVersionId }}</Badge>
      <Badge variant="outline" class="text-red-400">v{{ form.rightVersionId }}</Badge>
    </div>
  </Button>
</template>
